import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { BookmarkIcon, ChevronRightIcon, EyeIcon, EyeOffIcon, HeartIcon, Trash2Icon, UserMinusIcon } from 'lucide-react';
import { app, BridgeError, xhs, type BridgeAccount } from '@/lib/bridge';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { SelectRow } from '@/components/SelectRow';
import { PageSkeleton } from '@/components/skeletons';
import { TaskProgressCard } from '@/components/task-ui';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD, HINT } from '@/components/ui';
import { cn } from '@/lib/utils';
import { sleep, ToolNotices, useXhsTool, xhsErrorText } from './common';

/**
 * 薯薯 · 小红书互动清理（`/shushu/xhs/interactions?accountId=`）。
 *
 * 对照搬小书 `mobile/actions`（去掉额度）：取消点赞 / 取消收藏 / 取消关注在本页逐条执行，
 * 隐藏 / 显示 / 删除笔记交给原生任务引擎的整账号批量页（`/shushu/batch`）。
 * 与搬小书不同，点赞 / 收藏直接读线上列表（薯薯不备份点赞收藏）：每轮重读首页，处理过的会从列表消失，
 * 所以中断后重来也只需从头读。确认弹层逐条勾选；限流 / 风控 / 登录失效 / 连续失败 / 连续几轮没进展都会停下。
 */

const STEP_DELAY_MS = 1200; // 两次写操作之间
const UNFOLLOW_DELAY_MS = 1500; // 取关更敏感，间隔略长
const ROUND_DELAY_MS = 1500; // 处理完一页、重读下一页前
const CONSECUTIVE_FAIL_ABORT = 8;
const STAGNANT_ROUNDS = 3; // 连续几轮一条都没成功就停（接口混入删不掉的条目等）

type Target = { id: string; raw?: Record<string, unknown> };
type Counts = { collects: number | null; follows: number | null };

type LiveAction = {
  key: 'unlike' | 'uncollect' | 'unfollow';
  title: string;
  subtitle: string;
  icon: ReactNode;
  unit: string;
  confirmText: string;
  checklist: string[];
  delay: number;
  count: (c: Counts) => number | null;
  /** 读一轮待处理的条目（每次都从首页读） */
  fetch: (account: BridgeAccount) => Promise<Target[]>;
  run: (accountId: string, target: Target) => Promise<unknown>;
};

type BatchLink = { key: 'hide' | 'unhide' | 'delete'; title: string; subtitle: string; icon: ReactNode; tone: Tone };
type Tone = 'red' | 'blue' | 'gray';

async function notePage(account: BridgeAccount, path: string): Promise<Target[]> {
  const { data } = await xhs.request<{ notes?: { note_id?: string }[] }>(account.id, {
    path,
    query: { num: 30, cursor: '', user_id: account.uid ?? '', image_formats: 'jpg,webp,avif', xsec_token: '', xsec_source: '' },
  });
  return (data?.notes ?? []).flatMap((n) => (n.note_id ? [{ id: n.note_id }] : []));
}

/** intimacy_list 会混入聊天 / 互动用户，只对明确是关注关系的条目取关（与搬小书一致） */
function isFollowing(item: Record<string, unknown>): boolean {
  if (typeof item.isFollowing === 'boolean') return item.isFollowing;
  if (typeof item.followed === 'boolean') return item.followed;
  const status = Number(item.follow_status);
  if (item.follow_status !== undefined && Number.isFinite(status)) return status === 1;
  const relation = typeof item.relation === 'string' ? item.relation.toLowerCase() : '';
  return !(relation.includes('chat') || relation.includes('message') || relation === 'im');
}

function intimacyUserId(item: Record<string, unknown>): string | null {
  for (const key of ['userid', 'rid', 'user_id', 'id']) {
    const v = item[key];
    if (typeof v === 'string' && v) return v;
  }
  return null;
}

const LIVE_ACTIONS: LiveAction[] = [
  {
    key: 'unlike',
    title: '取消点赞',
    subtitle: '取消这个账号点过赞的笔记',
    icon: <HeartIcon className="size-4" />,
    unit: '个点赞',
    confirmText: '将逐页读取你点过赞的笔记并取消点赞，取消后无法自动恢复。',
    checklist: ['我了解这将取消该账号的全部点赞', '我确认要取消这些点赞'],
    delay: STEP_DELAY_MS,
    count: () => null,
    fetch: (account) => notePage(account, '/api/sns/web/v1/note/like/page'),
    run: (accountId, t) => xhs.unlikeNote(accountId, t.id),
  },
  {
    key: 'uncollect',
    title: '取消收藏',
    subtitle: '取消这个账号的全部收藏',
    icon: <BookmarkIcon className="size-4" />,
    unit: '个收藏',
    confirmText: '将逐页读取你的收藏并取消。需要保留的话，先用「导出收藏」把链接导出。',
    checklist: ['我了解这将取消该账号的全部收藏', '我确认要取消这些收藏'],
    delay: STEP_DELAY_MS,
    count: (c) => c.collects,
    fetch: (account) => notePage(account, '/api/sns/web/v2/note/collect/page'),
    run: (accountId, t) => xhs.uncollectNote(accountId, t.id),
  },
  {
    key: 'unfollow',
    title: '取消关注',
    subtitle: '取消该账号在小红书上的全部关注',
    icon: <UserMinusIcon className="size-4" />,
    unit: '个关注',
    confirmText: '将取消这个账号的全部关注，取消后需要手动重新关注。',
    checklist: ['我了解这将取消该账号在小红书上的全部关注', '我确认要取消这些关注'],
    delay: UNFOLLOW_DELAY_MS,
    count: (c) => c.follows,
    fetch: async (account) => {
      const { data } = await xhs.request<{ items?: Record<string, unknown>[] }>(account.id, {
        path: '/api/sns/web/v1/intimacy/intimacy_list',
      });
      return (data?.items ?? []).filter(isFollowing).flatMap((item) => {
        const id = intimacyUserId(item);
        return id ? [{ id, raw: item }] : [];
      });
    },
    run: (accountId, t) => xhs.unfollowUser(accountId, t.id),
  },
];

const BATCH_LINKS: BatchLink[] = [
  { key: 'hide', title: '隐藏笔记', subtitle: '将线上笔记全部设为仅自己可见', icon: <EyeOffIcon className="size-4" />, tone: 'gray' },
  { key: 'unhide', title: '显示笔记', subtitle: '将仅自己可见的笔记恢复为公开', icon: <EyeIcon className="size-4" />, tone: 'blue' },
  { key: 'delete', title: '删除笔记', subtitle: '永久删除账号内的发布笔记', icon: <Trash2Icon className="size-4" />, tone: 'red' },
];

const TONE: Record<Tone, string> = {
  red: 'text-destructive bg-destructive/10',
  blue: 'text-primary bg-primary/10',
  gray: 'text-muted-foreground bg-muted',
};

const GROUP_TITLE = 'text-muted-foreground -mb-2 px-4 text-footnote';

type Progress = {
  action: LiveAction;
  total: number;
  success: number;
  failed: number;
  status: 'running' | 'finished';
  stopReason?: string;
};

function ActionRow({
  icon,
  tone,
  title,
  subtitle,
  trailing,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  tone: Tone;
  title: string;
  subtitle: string;
  /** 右侧计数；不传显示箭头（跳到别的页） */
  trailing?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="bg-card flex w-full items-center gap-3 rounded-card px-4 py-3.5 text-left active:opacity-70 disabled:opacity-45"
      disabled={disabled}
      onClick={onClick}
    >
      <span className={cn('grid size-8 shrink-0 place-items-center rounded-icon', TONE[tone])}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-subheadline font-medium">{title}</span>
        <span className="text-muted-foreground mt-0.5 block text-caption1">{subtitle}</span>
      </span>
      {trailing ? (
        <span className="text-muted-foreground shrink-0 text-footnote">{trailing}</span>
      ) : (
        <ChevronRightIcon className="text-muted-foreground size-4 shrink-0" />
      )}
    </button>
  );
}

export function ShushuXhsInteractionsPage() {
  const tool = useXhsTool();
  const { account, usable, loaded } = tool;
  const [counts, setCounts] = useState<Counts>({ collects: null, follows: null });
  const [progress, setProgress] = useState<Progress | null>(null);
  const [confirming, setConfirming] = useState<LiveAction | null>(null);
  const [checked, setChecked] = useState<boolean[]>([]);
  const stopRef = useRef(false);
  useGroupedSurface();
  useAppReady(loaded);

  useEffect(() => {
    if (!usable || !account) return;
    let cancelled = false;
    xhs
      .request<{ follows?: unknown; tab_public?: { collectionNote?: { count?: unknown } } }>(account.id, {
        path: '/api/sns/web/v1/user/selfinfo',
      })
      .then(({ data }) => {
        if (cancelled) return;
        const num = (v: unknown) => (Number.isFinite(Number(v)) && v !== undefined && v !== '' ? Number(v) : null);
        setCounts({ collects: num(data?.tab_public?.collectionNote?.count), follows: num(data?.follows) });
      })
      .catch(() => {
        // 计数拿不到只影响展示
      });
    return () => {
      cancelled = true;
    };
  }, [usable, account]);

  const running = progress?.status === 'running';

  const execute = useCallback(
    async (action: LiveAction) => {
      if (!account) return;
      setConfirming(null);
      stopRef.current = false;
      const state: Progress = { action, total: action.count(counts) ?? 0, success: 0, failed: 0, status: 'running' };
      const publish = () => setProgress({ ...state, total: Math.max(state.total, state.success + state.failed) });
      publish();
      let consecutiveFails = 0;
      let stagnant = 0;
      const stop = (reason?: string) => {
        state.status = 'finished';
        state.stopReason = reason;
        publish();
        void app.haptic(reason ? 'error' : 'success');
      };
      try {
        for (;;) {
          if (stopRef.current) return stop('已手动暂停');
          const batch = await action.fetch(account);
          if (batch.length === 0) return stop();
          const before = state.success;
          for (const target of batch) {
            if (stopRef.current) return stop('已手动暂停');
            try {
              await action.run(account.id, target);
              state.success += 1;
              consecutiveFails = 0;
            } catch (e) {
              if (e instanceof BridgeError && ['rate_limited', 'risk_verification', 'login_required', 'account_banned'].includes(String(e.code))) {
                return stop(xhsErrorText(e));
              }
              state.failed += 1;
              consecutiveFails += 1;
              if (consecutiveFails >= CONSECUTIVE_FAIL_ABORT) return stop('连续多条失败，已停止。请稍后再试');
            }
            publish();
            await sleep(action.delay);
          }
          stagnant = state.success === before ? stagnant + 1 : 0;
          if (stagnant >= STAGNANT_ROUNDS) return stop('剩下的几条反复处理不成功（平台侧限制），已停止，可以稍后再试');
          await sleep(ROUND_DELAY_MS);
        }
      } catch (e) {
        stop(`读取列表失败：${xhsErrorText(e)}`);
      }
    },
    [account, counts],
  );

  const openConfirm = (action: LiveAction) => {
    setChecked(action.checklist.map(() => false));
    setConfirming(action);
    void app.haptic('light');
  };

  const openBatch = (key: BatchLink['key'], title: string) => {
    if (!account) return;
    void app.openPage(`/shushu/batch?accountId=${encodeURIComponent(account.id)}&action=${key}`, title);
  };

  const trailing = (action: LiveAction) => {
    const n = action.count(counts);
    return n === null ? '线上' : `${n} ${action.key === 'unfollow' ? '人' : '条'}`;
  };

  return (
    <PageShell title="互动清理" description="批量取消点赞 / 收藏 / 关注，整理笔记">
      {!loaded ? <PageSkeleton kind="actions" /> : null}
      <ToolNotices {...tool} />

      {loaded && account ? (
        <>
          <p className={GROUP_TITLE}>账号</p>
          <AccountCard account={account} showId={false} />
        </>
      ) : null}

      {loaded && usable ? (
        <>
          {progress ? (
            <>
              <p className={GROUP_TITLE}>{running ? '正在执行' : progress.stopReason ? '已暂停' : '本次结果'}</p>
              <TaskProgressCard
                title={progress.action.title}
                right={progress.total ? `${progress.success + progress.failed} / ${progress.total}` : `已处理 ${progress.success + progress.failed}`}
                ratio={progress.total ? (progress.success + progress.failed) / progress.total : 0}
                finished={!running && !progress.stopReason}
                paused={!running && !!progress.stopReason}
                action={
                  <>
                    <div className="text-muted-foreground flex gap-3.5 text-footnote tabular-nums">
                      <span className="text-green-600">成功 {progress.success}</span>
                      <span className="text-destructive">失败 {progress.failed}</span>
                    </div>
                    <p className={cn('mx-1', HINT, progress.stopReason && 'text-destructive')}>
                      {running
                        ? '请停留在本页面，退出会中断本次操作。'
                        : (progress.stopReason ?? `已完成，成功处理 ${progress.success} ${progress.action.unit}。`)}
                    </p>
                    <Button
                      variant="tinted"
                      className="w-full"
                      onClick={() => (running ? (stopRef.current = true) : setProgress(null))}
                    >
                      {running ? '暂停' : '关闭'}
                    </Button>
                  </>
                }
              />
            </>
          ) : null}

          <p className={GROUP_TITLE}>互动整理</p>
          <div className="flex flex-col gap-2">
            {LIVE_ACTIONS.map((action) => (
              <ActionRow
                key={action.key}
                icon={action.icon}
                tone="red"
                title={action.title}
                subtitle={action.subtitle}
                trailing={trailing(action)}
                disabled={running || action.count(counts) === 0}
                onClick={() => openConfirm(action)}
              />
            ))}
          </div>

          <p className={GROUP_TITLE}>笔记整理</p>
          <div className="flex flex-col gap-2">
            {BATCH_LINKS.map((link) => (
              <ActionRow
                key={link.key}
                icon={link.icon}
                tone={link.tone}
                title={link.title}
                subtitle={link.subtitle}
                disabled={running}
                onClick={() => openBatch(link.key, link.title)}
              />
            ))}
          </div>
          <p className={cn('mx-1 -mt-2', HINT)}>
            取消点赞 / 收藏 / 关注在本页逐条执行，请停留在本页；笔记整理在后台任务里跑，进度在任务中心也能看到。
          </p>
        </>
      ) : null}

      {/* 确认弹层（对照搬小书：逐条勾选，全部勾上才能开始） */}
      {confirming && account ? (
        <div className="animate-in fade-in-0 fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setConfirming(null)}>
          <div
            className="animate-in slide-in-from-bottom bg-background max-h-[85vh] w-full overflow-y-auto rounded-t-sheet px-4 pt-5 pb-[calc(16px+env(safe-area-inset-bottom))] duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-2 text-title3 font-semibold">{confirming.title}</h2>
            <p className="text-muted-foreground mb-3.5 text-subheadline leading-normal">
              账号「{account.nickname}」
              {confirming.count(counts) === null ? '：' : `共 ${confirming.count(counts)} ${confirming.key === 'unfollow' ? '人' : '条'}。`}
              {confirming.confirmText}
            </p>
            <Card className={cn(CARD, 'mb-3 py-0')}>
              <div className="flex flex-col ios:divide-y ios:divide-border">
                {confirming.checklist.map((text, index) => (
                  <SelectRow
                    key={text}
                    checked={checked[index] ?? false}
                    onCheckedChange={() => {
                      void app.haptic('light');
                      setChecked((prev) => prev.map((v, j) => (j === index ? !v : v)));
                    }}
                  >
                    {text}
                  </SelectRow>
                ))}
              </div>
            </Card>
            <div className="flex flex-col gap-2.5">
              <Button
                variant="destructive"
                className="disabled:bg-destructive/35 disabled:text-primary-foreground w-full"
                disabled={checked.some((v) => !v)}
                onClick={() => void execute(confirming)}
              >
                开始{confirming.title}
              </Button>
              <Button variant="secondary" className="w-full" onClick={() => setConfirming(null)}>
                取消
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
